import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  productsApi,
  type CatalogStatusUploadRow,
  type CreateCatalogEntryPayload,
  type CreateProductPayload,
  type DinSettingsInput,
  type PoolProductsPayload,
  type PreviewSplitPayload,
  type PreviewPoolPayload,
  type SplitProductPayload,
} from '@/api/productsApi'
import type {
  CatalogImportResult,
  CatalogStatusImportResult,
  CreatedProduct,
  ProductCatalogFilters,
  StorageTempMappingChange,
} from '@/types/products'
import { batchBySize, stripEmpty, type ImportProgress } from '@/lib/catalogImport'
import { useTenant } from '@/providers/TenantProvider'

// ─── Queries ────────────────────────────────────────────────────────────────

/**
 * One page of the ISBT 128 product-code catalog (Configure → Products).
 *
 * Paged because the table holds 14,911 codes. `placeholderData` keeps the
 * previous page on screen while the next one loads, so paging and typing in the
 * search box don't blank the table on every keystroke.
 *
 * The catalog is global reference data, but `tenantId` stays in the query key:
 * the request carries `X-Tenant-Id` for the permission check, and a user whose
 * role differs between tenants can be allowed here in one and refused in
 * another.
 */
export function useProductCatalog(filters: ProductCatalogFilters = {}) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: ['products', 'catalog', tenantId, filters],
    enabled: !!tenantId,
    queryFn: () => productsApi.listCatalog(tenantId as string, filters),
    placeholderData: previous => previous,
  })
}

/**
 * Distinct values for each filter dropdown, narrowed by the OTHER active
 * filters.
 *
 * Server-side because the picker used to derive these by scanning the whole
 * catalog in memory on every render — which was fine for a handful of curated
 * rows and is not for 14,911.
 */
export function useProductCatalogFacets(filters: ProductCatalogFilters = {}) {
  const { selectedTenantId: tenantId } = useTenant()
  // Paging must not re-fetch the dropdowns: the options depend on the filters,
  // not on which page of results is showing.
  const { page: _page, pageSize: _pageSize, ...facetFilters } = filters
  return useQuery({
    queryKey: ['products', 'catalog-facets', tenantId, facetFilters],
    enabled: !!tenantId,
    queryFn: () => productsApi.catalogFacets(tenantId as string, facetFilters),
    placeholderData: previous => previous,
  })
}

/**
 * Catalog entries for a set of ids, as a Map.
 *
 * For callers that hold a product and need the code it was made from. They used
 * to find it in the full catalog the picker had fetched; with 14,911 rows the
 * catalog is paged, so the entry has to be asked for by id.
 *
 * The ids are sorted into the query key so the same set in a different order is
 * one cache entry, not two.
 */
export function useCatalogEntriesByIds(ids: string[]) {
  const { selectedTenantId: tenantId } = useTenant()
  const unique = [...new Set(ids.filter(Boolean))].sort()
  const query = useQuery({
    queryKey: ['products', 'catalog-by-ids', tenantId, unique],
    enabled: !!tenantId && unique.length > 0,
    queryFn: () => productsApi.catalogEntriesByIds(tenantId as string, unique),
  })
  const byId = new Map((query.data ?? []).map(entry => [entry.id, entry]))
  return { ...query, byId }
}

/** Products already created for one submission — drives the completion gate. */
export function useCreatedProducts(submissionId: string | null) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: ['products', 'created', tenantId, submissionId],
    enabled: !!tenantId && !!submissionId,
    queryFn: () => productsApi.listCreatedProducts(tenantId as string, { submissionId: submissionId as string }),
  })
}

/**
 * The products associated with one form — its "This form" tab and the count on
 * the "P" icon (z8nrz7fr1b).
 *
 * Wider than `useCreatedProducts`, which stays the completion gate's question
 * ("did this form make a product"): this adds products the form split or
 * pooled, and every aliquot of those, wherever the split happened.
 */
export function useFormAssociatedProducts(submissionId: string | null) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: ['products', 'created', tenantId, 'associated', submissionId],
    enabled: !!tenantId && !!submissionId,
    queryFn: () =>
      productsApi.listCreatedProducts(tenantId as string, {
        associatedSubmissionId: submissionId as string,
      }),
  })
}

/**
 * The products one procedure has produced, across all of its forms.
 *
 * Used by the laboratory-order dialog to make the operator say WHICH BAG is
 * being tested. That choice is not cosmetic: the laboratory interface carries
 * no specimen identifier, so the order number we mint is the only thing that
 * will ever tie the result back to a product, and it can only do that if we
 * recorded which product it was placed for.
 */
export function useProcedureProducts(procedureId: string | null, enabled = true) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: ['products', 'created', tenantId, 'procedure', procedureId],
    enabled: enabled && !!tenantId && !!procedureId,
    queryFn: () =>
      productsApi.listCreatedProducts(tenantId as string, { procedureId: procedureId as string }),
  })
}

/**
 * Every product of one patient, across all of their procedures — the Products
 * dialog's "This patient" tab. `enabled` as for `useAllCreatedProducts`.
 */
export function usePatientProducts(patientMrn: string | null, enabled = true) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: ['products', 'created', tenantId, 'patient', patientMrn],
    enabled: enabled && !!tenantId && !!patientMrn,
    queryFn: () =>
      productsApi.listCreatedProducts(tenantId as string, { patientMrn: patientMrn as string }),
  })
}

/**
 * Every product created in this tenant — what the Products tab on /products
 * lists.
 *
 * Separate from `useCreatedProducts`, which is scoped to one submission and
 * drives that form's completion gate. Same endpoint, different question: "what
 * has this tenant made" vs "does this form still owe a product".
 *
 * `enabled` lets a dialog that is always mounted fetch only while open: the
 * case page mounts three of them, and loading every product in the tenant for
 * a closed dialog spent rate-limit allowance on nothing (z8nrz7e1c2).
 */
export function useAllCreatedProducts(enabled = true) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: ['products', 'created', tenantId, 'all'],
    enabled: enabled && !!tenantId,
    queryFn: () => productsApi.listCreatedProducts(tenantId as string),
  })
}

/**
 * What a split would do to a product's lab results, before it is performed.
 *
 * A QUERY rather than a mutation even though it POSTs: it creates nothing, and
 * the dialog re-asks as the operator changes the aliquot count or retypes a
 * volume, which is caching behaviour, not a command. The payload is in the key
 * for that reason.
 *
 * `enabled` is the caller's, so the request does not fire for a half-filled
 * form — the server would answer `no_aliquot_volumes` for every keystroke
 * between clearing a field and typing the next digit.
 */
export function useSplitPreview(
  parentId: string | null,
  payload: PreviewSplitPayload,
  enabled: boolean,
) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: ['products', 'split-preview', tenantId, parentId, payload],
    enabled: !!tenantId && !!parentId && enabled,
    queryFn: () => productsApi.previewSplit(tenantId as string, parentId as string, payload),
  })
}

/**
 * What pooling the current selection would produce.
 *
 * Same shape as `useSplitPreview`: the payload is in the query key because the
 * answer is a pure function of it, and `enabled` is the caller's so the
 * request does not fire for a selection of one — the server would answer
 * `too_few_members` for every checkbox on the way to two.
 */
export function usePoolPreview(payload: PreviewPoolPayload, enabled: boolean) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: ['products', 'pool-preview', tenantId, payload],
    enabled: !!tenantId && enabled,
    queryFn: () => productsApi.previewPool(tenantId as string, payload),
  })
}

// ─── Mutations ──────────────────────────────────────────────────────────────

export function useCreateCatalogEntry() {
  const { selectedTenantId: tenantId } = useTenant()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: CreateCatalogEntryPayload) =>
      productsApi.createCatalogEntry(tenantId as string, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products', 'catalog'] })
      queryClient.invalidateQueries({ queryKey: ['products', 'catalog-facets'] })
    },
  })
}

/**
 * Bulk import, sent in request-sized batches.
 *
 * The ISBT 128 file is 14,911 rows — about 15 MB of JSON in one POST, which the
 * API refuses outright: the backend configures no body-parser limit, so
 * Express's 100 kb default applies. `batchBySize` splits by MEASURED payload
 * size and `stripEmpty` drops the blank columns first (most columns are blank on
 * most rows), which together turn one impossible request into a few dozen small
 * ones. See src/lib/catalogImport.ts.
 *
 * Batches go one at a time rather than in parallel so the totals below stay
 * truthful and a failure stops the import instead of leaving an unknown number
 * of later batches in flight. Each batch is its own upsert, so a run that fails
 * part-way can simply be repeated — the earlier rows update rather than double.
 */
export function useImportCatalogEntries() {
  const { selectedTenantId: tenantId } = useTenant()
  const queryClient = useQueryClient()
  const [progress, setProgress] = useState<ImportProgress>({ done: 0, total: 0 })

  const mutation = useMutation({
    mutationFn: async (entries: CreateCatalogEntryPayload[]): Promise<CatalogImportResult> => {
      const batches = batchBySize(entries.map(stripEmpty))
      setProgress({ done: 0, total: entries.length })

      let created = 0
      let updated = 0
      let done = 0
      for (const batch of batches) {
        const result = await productsApi.importCatalogEntries(tenantId as string, batch)
        created += result.created
        updated += result.updated
        done += batch.length
        setProgress({ done, total: entries.length })
      }
      return { created, updated, total: entries.length }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products', 'catalog'] })
      queryClient.invalidateQueries({ queryKey: ['products', 'catalog-facets'] })
    },
  })

  return { ...mutation, progress }
}

// ─── Product code Active / Inactive (z8nrz7e72t) ────────────────────────────

/**
 * Every cached read that shows a code's status: the catalog pages, the facets
 * (a status filter narrows them), and the by-id lookups the split and pool
 * dialogs make.
 */
function invalidateCatalogStatus(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: ['products', 'catalog'] })
  queryClient.invalidateQueries({ queryKey: ['products', 'catalog-facets'] })
  queryClient.invalidateQueries({ queryKey: ['products', 'catalog-by-ids'] })
}

/** The backend's ceiling on ids per status request (`CATALOG_STATUS_MAX_IDS`). */
const CATALOG_STATUS_IDS_PER_REQUEST = 1000

/** Activate / Inactivate the given codes, and/or set their note — one row or a selection. */
export function useUpdateCatalogStatus() {
  const { selectedTenantId: tenantId } = useTenant()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      catalogEntryIds,
      ...change
    }: {
      catalogEntryIds: string[]
      active?: boolean
      note?: string
    }) => {
      // The server takes 1,000 ids a request; a selection kept across pages
      // can be larger.
      let updated = 0
      for (let i = 0; i < catalogEntryIds.length; i += CATALOG_STATUS_IDS_PER_REQUEST) {
        const ids = catalogEntryIds.slice(i, i + CATALOG_STATUS_IDS_PER_REQUEST)
        updated += (await productsApi.updateCatalogStatus(tenantId as string, { catalogEntryIds: ids, ...change }))
          .updated
      }
      return { updated }
    },
    onSuccess: () => invalidateCatalogStatus(queryClient),
  })
}

/** Activate ALL / Inactivate ALL — every code the filters match. */
export function useSetAllCatalogStatus() {
  const { selectedTenantId: tenantId } = useTenant()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ filters, active }: { filters: ProductCatalogFilters; active: boolean }) => {
      // Paging and order mean nothing to "all of them".
      const { page: _page, pageSize: _pageSize, sort: _sort, ...rest } = filters
      return productsApi.setAllCatalogStatus(tenantId as string, rest, active)
    },
    onSuccess: () => invalidateCatalogStatus(queryClient),
  })
}

/**
 * The Excel upload, in request-sized batches — the same 100 kb body limit that
 * shapes `useImportCatalogEntries` applies, and a full sheet is 14,911 rows.
 */
export function useImportCatalogStatus() {
  const { selectedTenantId: tenantId } = useTenant()
  const queryClient = useQueryClient()
  const [progress, setProgress] = useState<ImportProgress>({ done: 0, total: 0 })

  const mutation = useMutation({
    mutationFn: async (rows: CatalogStatusUploadRow[]): Promise<CatalogStatusImportResult> => {
      setProgress({ done: 0, total: rows.length })
      let updated = 0
      let done = 0
      const unknownCodes: string[] = []
      for (const batch of batchBySize(rows)) {
        const result = await productsApi.importCatalogStatus(tenantId as string, batch)
        updated += result.updated
        unknownCodes.push(...result.unknownCodes)
        done += batch.length
        setProgress({ done, total: rows.length })
      }
      return { updated, unknownCodes }
    },
    onSuccess: () => invalidateCatalogStatus(queryClient),
  })

  return { ...mutation, progress }
}

// ─── ISBT 128 DIN settings (z8nrz7fxwh) ──────────────────────────────────────

/** The tenant's DIN settings and the DIN the next product would get. */
export function useDinSettings() {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: ['din-settings', tenantId],
    enabled: !!tenantId,
    queryFn: () => productsApi.getDinSettings(tenantId as string),
  })
}

export function useSetDinSettings() {
  const { selectedTenantId: tenantId } = useTenant()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (settings: DinSettingsInput) => productsApi.setDinSettings(tenantId as string, settings),
    onSuccess: saved => queryClient.setQueryData(['din-settings', tenantId], saved),
  })
}

// ─── Storage temperature → storage condition mapping ────────────────────────

/** The tenant's mapping table and its unmapped count (the toolbar badge). */
export function useStorageTempMappings() {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: ['storage-temp-mappings', tenantId],
    enabled: !!tenantId,
    queryFn: () => productsApi.listStorageTempMappings(tenantId as string),
  })
}

/**
 * Save mapping changes in one request. Every catalog read carries the mapped
 * `storageCondition`, so those are refetched too — a product placed after this
 * must be offered units at the NEW condition.
 */
export function useSetStorageTempMappings() {
  const { selectedTenantId: tenantId } = useTenant()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (mappings: StorageTempMappingChange[]) =>
      productsApi.setStorageTempMappings(tenantId as string, mappings),
    // Returned so the mutation settles after the refetch: the dialog clears its
    // draft on success, and must not flash the old values in between.
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ['storage-temp-mappings'] }),
        queryClient.invalidateQueries({ queryKey: ['products', 'catalog'] }),
        queryClient.invalidateQueries({ queryKey: ['products', 'catalog-facets'] }),
        queryClient.invalidateQueries({ queryKey: ['products', 'catalog-by-ids'] }),
      ]),
  })
}

/**
 * Change a product's code. The server supersedes the product with a new one;
 * both rows change, so the product lists and the trails are refetched.
 */
export function useChangeProductCode() {
  const { selectedTenantId: tenantId } = useTenant()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ productId, ...payload }: { productId: string; catalogEntryId: string; reason: string }) =>
      productsApi.changeProductCode(tenantId as string, productId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products', 'created'] })
      queryClient.invalidateQueries({ queryKey: ['products', 'one'] })
      queryClient.invalidateQueries({ queryKey: ['products', 'audit'] })
      // The slot's occupant changed from the old row to the new one.
      queryClient.invalidateQueries({ queryKey: ['storage-nodes'] })
    },
  })
}

export function useCreateProduct() {
  const { selectedTenantId: tenantId } = useTenant()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: CreateProductPayload) =>
      productsApi.createProduct(tenantId as string, payload),
    onSuccess: created => {
      // Write the known-fresh result straight into this submission's query key
      // rather than relying solely on invalidateQueries' background refetch —
      // that refetch is a real network round-trip, and the completion gate
      // (useCreatedProducts) can otherwise read the still-stale empty cache if
      // Complete Form is clicked right after the label preview closes.
      queryClient.setQueryData<CreatedProduct[]>(
        ['products', 'created', tenantId, created.submissionId],
        prev => [...(prev ?? []), created],
      )
      queryClient.invalidateQueries({ queryKey: ['products', 'created'] })
      // A minted DIN advanced the tenant's sequence.
      queryClient.invalidateQueries({ queryKey: ['din-settings'] })
    },
  })
}

export function useSplitProduct() {
  const { selectedTenantId: tenantId } = useTenant()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ parentId, payload }: { parentId: string; payload: SplitProductPayload }) =>
      productsApi.splitProduct(tenantId as string, parentId, payload),
    onSuccess: children => {
      // Same reasoning as useCreateProduct: write the known-fresh children in
      // directly rather than trusting the background refetch to land before
      // the label-print / storage-assignment chain that follows reads them.
      const submissionId = children[0]?.submissionId
      if (submissionId) {
        queryClient.setQueryData<CreatedProduct[]>(
          ['products', 'created', tenantId, submissionId],
          prev => [...(prev ?? []), ...children],
        )
      }
      queryClient.invalidateQueries({ queryKey: ['products', 'created'] })
    },
  })
}

export function usePoolProducts() {
  const { selectedTenantId: tenantId } = useTenant()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: PoolProductsPayload) =>
      productsApi.poolProducts(tenantId as string, payload),
    onSuccess: pooled => {
      // Same reasoning as useCreateProduct: seed the known-fresh row so the
      // label/storage chain that follows doesn't read a stale cache.
      queryClient.setQueryData<CreatedProduct[]>(
        ['products', 'created', tenantId, pooled.submissionId],
        prev => [...(prev ?? []), pooled],
      )
      queryClient.invalidateQueries({ queryKey: ['products', 'created'] })
      queryClient.invalidateQueries({ queryKey: ['din-settings'] })
    },
  })
}

/**
 * One product's append-only trail — what happened to this bag.
 *
 * Returns the same unified row shape as the form-level trail
 * (`useSubmissionAuditTrail`), so both render through `AuditTrailTable`.
 */
/** One product, for the detail page. */
export function useProduct(productId: string | null) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: ['products', 'one', tenantId, productId],
    enabled: !!tenantId && !!productId,
    queryFn: () => productsApi.getProduct(tenantId as string, productId as string),
  })
}

/** The product page's record: catalog, divisions, run, release, storage (z8nrz7g57f). */
export function useProductRecord(productId: string | null) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: ['products', 'record', tenantId, productId],
    enabled: !!tenantId && !!productId,
    queryFn: () => productsApi.getProductRecord(tenantId as string, productId as string),
  })
}

export function useProductAudit(productId: string | null) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: ['products', 'audit', tenantId, productId],
    enabled: !!tenantId && !!productId,
    queryFn: () => productsApi.listProductAudit(tenantId as string, productId as string),
  })
}

export function useUpdateProductVolume() {
  const { selectedTenantId: tenantId } = useTenant()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      productId,
      ...payload
    }: {
      productId: string
      volumeMl: number
      reason: string
      reasonDetail?: string
    }) => productsApi.updateProductVolume(tenantId as string, productId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products', 'created'] })
      queryClient.invalidateQueries({ queryKey: ['products', 'audit'] })
    },
  })
}
